import Form from "react-bootstrap/Form";
import { updateGroupFeedback } from "../../validation/validityStyling";

const resolveDateTag = (value) => {
  if (value !== "[TODAY]") {
    return value;
  }

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const daysInMonth = (monthNum, yearNum) =>
  new Date(yearNum ?? 2000, monthNum, 0).getDate();

// Recomputes a date group's combined hidden value straight from whatever is
// currently in its day/month/year/time boxes. Reads min/max off the
// container itself (rather than closing over the question) so this can be
// called from anywhere - including a value-setting path that never fires a
// part's own onChange/onBlur, such as browser autofill filling several
// fields at once without dispatching an event for each one.
export const syncDateValue = (container) => {
  const min = container.dataset.minDate;
  const max = container.dataset.maxDate;
  const day = container.querySelector('[data-date-part="day"]');
  const month = container.querySelector('[data-date-part="month"]');
  const year = container.querySelector('[data-date-part="year"]');
  const time = container.querySelector('[data-date-part="time"]');
  const hidden = container.querySelector('[data-date-part="value"]');

  const dayNum = day && day.value ? Number(day.value) : null;
  const monthNum = month.value ? Number(month.value) : null;
  const yearNum = year && year.value.length === 4 ? Number(year.value) : null;

  const isDateComplete =
    monthNum !== null &&
    (!year || yearNum !== null) &&
    (!day || dayNum !== null);

  // ExcludeYear questions have no year field to read, but the output
  // must still be a full date - "0001" stands in for the omitted year.
  // ExcludeDayOfMonth questions likewise have no day field - the last day
  // of the selected month stands in for the omitted day.
  const dateIso = isDateComplete
    ? [
        year ? year.value : "0001",
        month.value.padStart(2, "0"),
        day
          ? day.value.padStart(2, "0")
          : String(daysInMonth(monthNum, yearNum)).padStart(2, "0"),
      ]
        .filter(Boolean)
        .join("-")
    : "";

  // Tracked per field so only the part(s) actually at fault get flagged,
  // rather than blanket-marking the whole group invalid.
  let monthMessage = "";
  let dayMessage = "";
  let yearMessage = "";
  let rangeMessage = "";

  if (monthNum !== null && (monthNum < 1 || monthNum > 12)) {
    monthMessage = "Month must be between 01 and 12.";
  } else if (dayNum !== null && monthNum !== null) {
    const maxDay = daysInMonth(monthNum, yearNum);
    if (dayNum < 1 || dayNum > maxDay) {
      dayMessage = `Day must be between 1 and ${maxDay} for the selected month.`;
    }
  } else if (dayNum !== null && (dayNum < 1 || dayNum > 31)) {
    dayMessage = "Day must be between 1 and 31.";
  }

  // No native constraint stops a 1-3 digit year from passing checkValidity
  // (required only checks for non-empty) - without this, a partial year
  // looks perfectly valid to the user while silently producing no date at
  // all, since isDateComplete already requires a full 4-digit year.
  if (year && year.value && year.value.length !== 4) {
    yearMessage = "Year must be exactly 4 digits.";
  }

  // Min/max comparisons need a full year+day date to be meaningful, so
  // they're skipped when either part is excluded from the question.
  // The whole date (not one field) is out of range, so every present
  // part is flagged.
  if (year && day && !monthMessage && !dayMessage && isDateComplete) {
    if (min && dateIso < min) {
      rangeMessage = `Date must be on or after ${min}.`;
    } else if (max && dateIso > max) {
      rangeMessage = `Date must be on or before ${max}.`;
    }
  }

  // Only ever output a date that passed validation. Time is appended
  // whenever the user has entered one, independently of whether the
  // date itself is required - entered, valid data is never dropped.
  const isDateValid =
    isDateComplete &&
    !monthMessage &&
    !dayMessage &&
    !yearMessage &&
    !rangeMessage;
  const hasTimeValue = !!(time && time.value);
  hidden.value = isDateValid
    ? hasTimeValue
      ? `${dateIso}T${time.value}`
      : dateIso
    : "";

  [
    { input: day, message: dayMessage || rangeMessage },
    { input: month, message: monthMessage || rangeMessage },
    { input: year, message: yearMessage || rangeMessage },
  ]
    .filter((field) => field.input)
    .forEach(({ input, message }) => {
      input.setCustomValidity(message);
      const newClass = input.className
        .replace("is-invalid", "")
        .replace("is-valid", "")
        .trimEnd();
      input.className = message
        ? `${newClass} is-invalid`
        : isDateComplete
          ? `${newClass} is-valid`
          : newClass;
    });

  updateGroupFeedback(month);
};

const buildDateQuestion = (question) => {
  const min = resolveDateTag(question.MinDate);
  const max = resolveDateTag(question.MaxDate);

  const handlePartInput = (maxLength) => (event) => {
    event.target.value = event.target.value
      .replace(/\D/g, "")
      .slice(0, maxLength);
    syncDateValue(event.target.closest(".date-input-group"));
  };

  const handlePartBlur = (padLength) => (event) => {
    if (event.target.value) {
      event.target.value = event.target.value.padStart(padLength, "0");
    }
    syncDateValue(event.target.closest(".date-input-group"));
  };

  // Year isn't zero-padded on blur like day/month (a partial year has no
  // sensible completion), but blurring it should still force a recompute -
  // e.g. a value set by autofill on the other fields with no synthetic event
  // of its own only gets picked up once something in the group re-syncs.
  const handleGroupBlur = (event) => {
    syncDateValue(event.target.closest(".date-input-group"));
  };

  const handleTimeChange = (event) => {
    syncDateValue(event.target.closest(".date-input-group"));
  };

  return (
    <div
      className="d-flex gap-2 date-input-group"
      data-min-date={min || ""}
      data-max-date={max || ""}
    >
      {!question.ExcludeDayOfMonth && (
        <Form.Control
          type="text"
          inputMode="numeric"
          placeholder="DD"
          aria-label="Day"
          autoComplete="off"
          data-date-part="day"
          maxLength={2}
          style={{ width: "4.5rem" }}
          required={question.IsMandatory}
          onChange={handlePartInput(2)}
          onBlur={handlePartBlur(2)}
        />
      )}
      <Form.Control
        type="text"
        inputMode="numeric"
        placeholder="MM"
        aria-label="Month"
        autoComplete="off"
        data-date-part="month"
        maxLength={2}
        style={{ width: "4.5rem" }}
        required={question.IsMandatory}
        onChange={handlePartInput(2)}
        onBlur={handlePartBlur(2)}
      />
      {!question.ExcludeYear && (
        <Form.Control
          type="text"
          inputMode="numeric"
          placeholder="YYYY"
          aria-label="Year"
          autoComplete="off"
          data-date-part="year"
          maxLength={4}
          style={{ width: "6rem" }}
          required={question.IsMandatory}
          onChange={handlePartInput(4)}
          onBlur={handleGroupBlur}
        />
      )}
      {question.IncludeTime && (
        <Form.Control
          type="time"
          aria-label="Time"
          autoComplete="off"
          data-date-part="time"
          style={{ width: "8rem" }}
          required={question.IsMandatory}
          onChange={handleTimeChange}
        />
      )}
      <input type="hidden" name={question.Name} data-date-part="value" />
    </div>
  );
};

export default buildDateQuestion;
